/*
  The saved-items page.

  Saved entries are handles, so the list is built by asking the storefront for
  each product's real card through the Section Rendering API rather than by
  rebuilding a card in JavaScript. See `sections/roots-wishlist-card.liquid` for
  why that matters.

  Two states this has to get right, because both are ordinary rather than
  exceptional:

  - EMPTY. The common case for a first visit, and the only place a shopper is
    told the feature exists at all.
  - GONE. A saved product can be unpublished or deleted between visits, in which
    case its card 404s. The entry is dropped and the shopper is told once, rather
    than being left with a silent gap in the grid or a row that fails forever.
*/

class RootsWishlistPage extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-wishlist-grid]');
    this.empty = this.querySelector('[data-wishlist-empty]');
    this.loading = this.querySelector('[data-wishlist-loading]');
    this.note = this.querySelector('[data-wishlist-note]');
    this.live = this.querySelector('[data-wishlist-live]');
    this.rootUrl = this.dataset.rootUrl || '/';

    this.render = this.render.bind(this);

    if (!window.RootsWishlist) {
      // The store module is loaded globally and before this, so this only
      // happens if that script failed. Show the empty state rather than an
      // eternal spinner.
      this.show('empty');
      return;
    }

    window.RootsWishlist.subscribe(this.render);
    this.render();
  }

  show(state) {
    if (this.loading) this.loading.hidden = state !== 'loading';
    if (this.empty) this.empty.hidden = state !== 'empty';
    if (this.grid) this.grid.hidden = state !== 'list';
    if (this.note) this.note.hidden = state !== 'list';
  }

  async render() {
    const items = window.RootsWishlist.all();

    if (!items.length) {
      if (this.grid) this.grid.replaceChildren();
      this.show('empty');
      return;
    }

    // Only show the spinner on a genuinely empty grid. Re-rendering after a
    // removal should not blank the list the shopper is looking at.
    if (!this.grid.children.length) this.show('loading');

    const results = await Promise.all(items.map((item) => this.fetchCard(item)));

    const missing = [];
    const fragment = document.createDocumentFragment();

    results.forEach((html, index) => {
      const item = items[index];
      if (html === null) {
        missing.push(item);
        return;
      }
      // A network failure resolves to '' — the entry is kept for the next
      // render, but appending it now would put an empty cell in the grid.
      if (html === '') return;
      const li = document.createElement('li');
      li.className = 'grid__item roots-wishlist-grid__item';
      li.dataset.handle = item.handle;
      li.innerHTML = html;
      fragment.append(li);
    });

    this.grid.replaceChildren(fragment);

    if (missing.length) {
      missing.forEach((item) => window.RootsWishlist.remove(item.handle));
      if (this.live) this.live.textContent = this.dataset.unavailableText || '';
      // `remove` fires the change event, which re-enters render() with the
      // pruned list — so return here rather than deciding the state twice.
      return;
    }

    this.show(this.grid.children.length ? 'list' : 'empty');
  }

  async fetchCard(item) {
    const url = `${this.rootUrl}products/${encodeURIComponent(item.handle)}?section_id=roots-wishlist-card`;
    try {
      const response = await fetch(url);
      if (!response.ok) return null;
      const text = await response.text();
      // The Section Rendering API wraps the section in its own
      // `<div id="shopify-section-…">`. Unwrap it so the card is a direct child
      // of the grid item and the product-grid layout still applies to it.
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const wrapper = doc.querySelector('[id^="shopify-section-"]');
      const markup = (wrapper || doc.body).innerHTML.trim();
      return markup || null;
    } catch (error) {
      // A network failure is not a deleted product — keep the entry and let the
      // next render try again.
      return '';
    }
  }
}

if (!customElements.get('roots-wishlist-page')) {
  customElements.define('roots-wishlist-page', RootsWishlistPage);
}
